/**
 * Migrate Legacy Trip Data from localStorage to IndexedDB
 *
 * This migration runs once to convert pre-existing localStorage-only trip data
 * into the new IndexedDB-based multi-trip system.
 */

import storageManager from './storageManager.js';

const MIGRATION_FLAG = 'legacy_trip_migrated_v1';

/**
 * Check if migration has already run
 */
function isMigrationComplete() {
  return localStorage.getItem(MIGRATION_FLAG) === 'true';
}

/**
 * Mark migration as complete
 */
function markMigrationComplete() {
  localStorage.setItem(MIGRATION_FLAG, 'true');
}

/**
 * Get legacy trip data from localStorage
 */
function getLegacyTripData() {
  try {
    // Get user data which contains destination/country
    const userDataKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('user_') && key !== 'user_settings') {
        userDataKeys.push(key);
      }
    }

    let userData = null;
    if (userDataKeys.length > 0) {
      const userDataStr = localStorage.getItem(userDataKeys[0]);
      if (userDataStr) {
        userData = JSON.parse(userDataStr);
      }
    }

    // Fallback: check userSettings
    if (!userData || !userData.destination) {
      const settingsStr = localStorage.getItem('userSettings');
      if (settingsStr) {
        userData = JSON.parse(settingsStr);
      }
    }

    // Get trip data (photos, notes, etc.)
    const tripDataStr = localStorage.getItem('tripData');
    const tripData = tripDataStr ? JSON.parse(tripDataStr) : null;

    return { userData, tripData };
  } catch (error) {
    console.error('[Migration] Error reading legacy data:', error);
    return { userData: null, tripData: null };
  }
}

/**
 * Main migration function
 */
export async function migrateLegacyTrip() {
  // Skip if already migrated
  if (isMigrationComplete()) {
    console.log('[Migration] Legacy trip already migrated');
    return { migrated: false, reason: 'already_migrated' };
  }

  console.log('[Migration] Checking for legacy trip data...');

  try {
    // Get legacy data
    const { userData, tripData } = getLegacyTripData();

    // Check if there's any legacy data to migrate
    if (!userData || (!userData.destination && !userData.location)) {
      console.log('[Migration] No legacy trip data found');
      markMigrationComplete();
      return { migrated: false, reason: 'no_data' };
    }

    // Initialize storageManager
    await storageManager.initialize();

    // Check if trips already exist in IndexedDB
    const existingTrips = await storageManager.getAllTrips();
    if (existingTrips.length > 0) {
      console.log('[Migration] Trips already exist in IndexedDB, skipping migration');
      markMigrationComplete();
      return { migrated: false, reason: 'trips_exist' };
    }

    // Build trip object from legacy data
    const destination = userData.destination || userData.location || 'My Trip';
    const country = userData.country || 'Country';

    const legacyTrip = {
      tripId: storageManager.generateTripId(),
      destination: {
        city: destination,
        country: country
      },
      dates: {
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        currentDay: 1,
        totalDays: 1
      },
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      migratedFrom: 'localStorage'
    };

    console.log('[Migration] Migrating legacy trip:', legacyTrip);

    // Save trip to IndexedDB
    await storageManager.saveTrip(legacyTrip);

    // Update trips index
    const index = storageManager.getTripsIndex();
    index.activeTrip = legacyTrip.tripId;
    index.tripIds = [legacyTrip.tripId];
    index.lastModified = new Date().toISOString();
    storageManager.saveTripsIndex(index);

    // Migrate photos to IndexedDB if they exist
    if (tripData && tripData.photos && tripData.photos.length > 0) {
      console.log(`[Migration] Migrating ${tripData.photos.length} photos...`);

      for (const photo of tripData.photos) {
        try {
          // Convert base64 to blob if needed
          let blob = photo.data;
          if (typeof photo.data === 'string' && photo.data.startsWith('data:')) {
            const response = await fetch(photo.data);
            blob = await response.blob();
          }

          await storageManager.savePhoto({
            id: photo.id || storageManager.generatePhotoId(),
            tripId: legacyTrip.tripId,
            blob: blob,
            timestamp: photo.timestamp || new Date().toISOString()
          });
        } catch (photoError) {
          console.warn('[Migration] Failed to migrate photo:', photoError);
        }
      }
    }

    // Mark migration complete
    markMigrationComplete();

    console.log('[Migration] ✅ Legacy trip migrated successfully!');

    return {
      migrated: true,
      trip: legacyTrip,
      photosMigrated: tripData?.photos?.length || 0
    };

  } catch (error) {
    console.error('[Migration] Migration failed:', error);
    // Don't mark as complete if failed - allow retry
    return { migrated: false, reason: 'error', error: error.message };
  }
}

export default migrateLegacyTrip;
